import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { TimberService } from '@logging/timber.service';
import { ChannelNames } from '@itero/itero-client-communication';
import { NavigationInfo, ShellCommunicationEvents } from '@shared/generalInterfaces';
import { Subscription, map, take, tap, of } from 'rxjs';
import { timer } from 'rxjs/internal/observable/timer';
import { Consts } from '@shared/consts';
import { getCopy } from 'app/extensions/map-extensions';
import { KnownComponentsNames } from '@core/components-names';
import { NavigationStore } from './state/navigation-store';
import { NavigationQuery } from './state/navigation-query';
import { CommunicationService } from 'app/services/client-communication/communication.service';
import { EventName } from '@itero/itero-client-communication';
import { isStandardRemoteApplication } from 'app/remote-application/helpers/is-standard-remote-application';
import { MFE_SUB_PATHS, STANDARD_REMOTE_URL_PREFIX } from 'app/remote-application/consts';
import { RemoteMfeNames } from 'app/enums/remote-mfe.enum';
import { OrdersService } from '@shared/orders/orders.service';
import { Pages } from '@logging/interfaces/bi.enums';

@Injectable({ providedIn: 'root' })
export class ApplicationNavigationService {
	readonly domain = window.location.origin;
	private intervalForClosingApp = 10000;
	private closingAppSubscriptions = new Map<string, Subscription>();
	closingTimerApps: KnownComponentsNames[] = [KnownComponentsNames.AohsComponent, KnownComponentsNames.IteroAxiWrapper];

	constructor(
		private router: Router,
		private globalSettingsService: GlobalSettingsService,
		private timberService: TimberService,
		private ordersService: OrdersService,
		private communicationService: CommunicationService,
		private store: NavigationStore,
		private query: NavigationQuery
	) {}

	reset(): void {
		this.store.reset();
	}

	// transition: NavigationInfo
	pushTransition(transition: NavigationInfo): void {
		this.store.pushTransition(transition);
	}

	popTransition(): NavigationInfo {
		const lastTransition = this.peekTransition();

		this.store.removeTransition();

		return lastTransition;
	}

	peekTransition(): NavigationInfo {
		return this.query.peekTransition();
	}

	startClosingAppTimer(transition: NavigationInfo) {
		const subscription = timer(this.intervalForClosingApp)
			.pipe(
				take(1),
				tap(_ => this.closingAppSubscriptions.delete(transition.navigatedToAppId))
			)
			.subscribe(() => {
				this.router.navigateByUrl(transition.urlFrom);
			});

		this.closingAppSubscriptions.set(transition.navigatedToAppId, subscription);
	}

	stopClosingAppTimer(appId: string) {
		this.closingAppSubscriptions.get(appId)?.unsubscribe();
		this.closingAppSubscriptions.delete(appId);
	}

	navigateToHomePageWhenTransitionNotFound(backFromAppId: string) {
		this.timberService.error(`Navigation back from ${backFromAppId} failed.`, { module: 'ApplicationNavigationService' });
		this.navigateToHomePage();
	}

	navigateToHomePageWhenStackIsEmpty() {
		this.timberService.error('Navigation stack is empty when user clicked back button.', { module: 'ApplicationNavigationService' });
		this.navigateToHomePage();
	}

	handleBackClicked() {
		const currentTransition = this.peekTransition();

		if (!currentTransition) {
			this.navigateToHomePageWhenStackIsEmpty();
		} else if (currentTransition.navigatedFromAppId.includes('login')) {
			this.reset();
			this.navigateToHomePage();
		} else if (this.closingTimerApps.includes(currentTransition.navigatedToAppId)) {
			this.startClosingAppTimer(currentTransition);
			void this.communicationService.publishEvent(
				ChannelNames.applicationChannel,
				'app-shell',
				ShellCommunicationEvents.PrepareToClose,
				[currentTransition.navigatedToAppId]
			);
		} else {
			// Navigation within the same MFE that runs in its own runtime: host routing cannot drive the MFE,
			// so send apply-navigation-state to the MFE instead.
			if (currentTransition.navigatedFromAppId === currentTransition.navigatedToAppId) {
				void this.communicationService.publishEvent(
					ChannelNames.applicationChannel,
					'app-shell',
					EventName.applyNavigationState,
					[currentTransition.navigatedFromAppId],
					{
						navigateFromAppId: currentTransition.navigatedFromAppId,
						navigateToAppId: currentTransition.navigatedToAppId,
						urlTo: `${this.domain}${currentTransition.urlFrom}`,
					}
				);
			}
			this.router.navigateByUrl(currentTransition.urlFrom);
		}
	}

	navigateToStandardRemoteApplication(appId: string, appParameters?: Record<string, unknown>) {
		if (appId in MFE_SUB_PATHS) {
			const rolePath = this.globalSettingsService.rolePath();
			const subPath = MFE_SUB_PATHS[appId as RemoteMfeNames];

			void this.router.navigate([rolePath, Pages.Settings, STANDARD_REMOTE_URL_PREFIX, appId, subPath], {
				state: { ...appParameters, openingPage: subPath },
			});

			return;
		}

		return this.router.navigate([STANDARD_REMOTE_URL_PREFIX, appId], appParameters ? { state: appParameters } : undefined);
	}

	navigate(transition: NavigationInfo) {
		if (isStandardRemoteApplication(transition.navigatedToAppId)) {
			this.navigateToStandardRemoteApplication(
				transition.navigatedToAppId,
				transition.appMetadata != null ? JSON.parse(transition.appMetadata) : undefined
			);

			return;
		}

		const navigationCommands = Consts.NavigationCommands[getCopy](transition.navigatedToAppId);

		this.addCommands(transition.navigatedToAppId, navigationCommands, transition.appMetadata)
			.pipe(
				tap(commands => {
					const queryParams = this.getQueryParams(transition);

					if (queryParams) {
						this.router.navigate(commands, { queryParams });
					} else {
						this.router.navigate(commands);
					}
				}),
				take(1)
			)
			.subscribe();
	}

	navigateBack(backFromAppId: string) {
		const transition = this.peekTransition();

		if (transition) {
			this.stopClosingAppTimer(transition.navigatedToAppId);
			this.router.navigateByUrl(transition.urlFrom);
		} else {
			this.navigateToHomePageWhenTransitionNotFound(backFromAppId);
		}
	}

	navigateToHomePage() {
		const roleType = this.globalSettingsService.rolePath();

		this.router.navigate([`${roleType}/home`]);
	}

	private addCommands(appId: string, commands: string[], stringAppMetadata: string) {
		switch (appId) {
			case 'iosim': {
				const appMetadata = JSON.parse(stringAppMetadata);

				return this.ordersService.getOrderCode(appMetadata.orderId).pipe(
					take(1),
					map(orderCode => {
						commands.push(orderCode);
						commands.push(appMetadata.orderId);

						return commands;
					})
				);
			}
			default:
				return of(commands);
		}
	}

	private getQueryParams(transition: NavigationInfo) {
		switch (transition.navigatedToAppId) {
			case 'iosim': {
				return { returnUrl: transition.urlFrom }; /// check here as removed returnUrl
			}
			default:
				return {};
		}
	}
}
